import { createTheme } from '@mui/material/styles'

/**
 * Thème TicketNGo Scan.
 *
 * Contraintes de design :
 * - utilisé à 99% sur mobile, souvent en extérieur, parfois avec des gants,
 *   par des employés qui répètent le même geste des centaines de fois ;
 * - interface sombre : lisible de nuit, moins éblouissante, met en valeur
 *   le flux caméra et les verdicts colorés ;
 * - toutes les cibles tactiles font au moins 48px (recommandation WCAG 2.5.5) ;
 * - les tailles de texte sont fluides (clamp) pour couvrir de l'iPhone SE
 *   (320px) aux tablettes sans point de rupture visible.
 */

// Couleurs de marque extraites du logo
export const BRAND = {
  orange: '#E84E1B',
  orangeDark: '#C43F13',
  orangeLight: '#FF7A4D',
  ink: '#1D1D1B',
}

// Échelle de gris neutre légèrement chaude, accordée au orange de la marque
const NEUTRAL = {
  900: '#0E0F11',
  850: '#141619',
  800: '#1A1D21',
  700: '#23272C',
  600: '#2F343A',
  500: '#454C54',
  400: '#6B7480',
  300: '#98A1AC',
  200: '#C4CBD3',
  100: '#E4E8ED',
  50: '#F4F6F8',
}

export const SEMANTIC = {
  valid: '#12B76A',
  validDark: '#027A48',
  validSurface: '#052E1C',
  invalid: '#F04438',
  invalidDark: '#B42318',
  invalidSurface: '#3A0D0A',
  warning: '#F79009',
  warningDark: '#B54708',
  warningSurface: '#3A2405',
}

// Tailles fluides : min sur petit écran, max sur grand écran.
const fluid = (minPx, maxPx, minVw = 360, maxVw = 768) =>
  `clamp(${minPx / 16}rem, calc(${minPx / 16}rem + ${
    ((maxPx - minPx) * 100) / (maxVw - minVw)
  }vw - ${(((maxPx - minPx) * minVw) / (maxVw - minVw) / 16).toFixed(4)}rem), ${maxPx / 16}rem)`

const FONT_STACK = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',')

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND.orange,
      dark: BRAND.orangeDark,
      light: BRAND.orangeLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: NEUTRAL[200],
      contrastText: NEUTRAL[900],
    },
    success: {
      main: SEMANTIC.valid,
      dark: SEMANTIC.validDark,
      contrastText: '#FFFFFF',
    },
    error: {
      main: SEMANTIC.invalid,
      dark: SEMANTIC.invalidDark,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: SEMANTIC.warning,
      dark: SEMANTIC.warningDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: NEUTRAL[900],
      paper: NEUTRAL[800],
    },
    text: {
      primary: '#FFFFFF',
      secondary: NEUTRAL[300],
      disabled: NEUTRAL[400],
    },
    divider: 'rgba(255,255,255,0.10)',
    neutral: NEUTRAL,
  },

  shape: { borderRadius: 14 },

  // Grille de 8px : suffisamment dense sur mobile, jamais tassée
  spacing: 8,

  breakpoints: {
    values: { xs: 0, sm: 480, md: 768, lg: 1024, xl: 1280 },
  },

  typography: {
    fontFamily: FONT_STACK,
    // Le verdict d'un scan doit être lisible à bout de bras, en plein soleil
    h1: { fontSize: fluid(40, 64), fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 },
    h2: { fontSize: fluid(32, 48), fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 },
    h3: { fontSize: fluid(26, 36), fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h4: { fontSize: fluid(21, 28), fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h5: { fontSize: fluid(18, 22), fontWeight: 700, lineHeight: 1.3 },
    h6: { fontSize: fluid(16, 18), fontWeight: 700, lineHeight: 1.35 },
    subtitle1: { fontSize: fluid(15, 17), fontWeight: 600, lineHeight: 1.4 },
    subtitle2: { fontSize: fluid(13, 15), fontWeight: 600, lineHeight: 1.4, color: NEUTRAL[300] },
    body1: { fontSize: fluid(15, 17), lineHeight: 1.5 },
    body2: { fontSize: fluid(13, 15), lineHeight: 1.5 },
    caption: { fontSize: fluid(12, 13), lineHeight: 1.4, letterSpacing: '0.01em' },
    overline: {
      fontSize: fluid(11, 12),
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          // Hauteur de viewport stable malgré les barres d'URL mobiles
          '--app-vh': '100dvh',
          '--safe-top': 'env(safe-area-inset-top, 0px)',
          '--safe-bottom': 'env(safe-area-inset-bottom, 0px)',
          '--safe-left': 'env(safe-area-inset-left, 0px)',
          '--safe-right': 'env(safe-area-inset-right, 0px)',
          colorScheme: 'dark',
        },
        '@supports not (height: 100dvh)': {
          ':root': { '--app-vh': '100vh' },
        },
        'html, body, #root': {
          height: '100%',
          overscrollBehavior: 'none',
        },
        body: {
          backgroundColor: NEUTRAL[900],
          // Évite le surlignage bleu au tap et la sélection accidentelle
          WebkitTapHighlightColor: 'transparent',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        // Supprime le délai de 300ms et le double-tap zoom sur les contrôles
        'button, [role="button"], a, input, select, textarea': {
          touchAction: 'manipulation',
        },
        'h1, h2, h3, h4, h5, h6, p, span, label': {
          overflowWrap: 'anywhere',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 52,
          borderRadius: 14,
          paddingInline: 20,
          fontSize: '1rem',
          // Le retour tactile compense l'absence de haptique en PWA
          transition: 'transform 120ms ease, background-color 160ms ease',
          '&:active': { transform: 'scale(0.975)' },
        },
        sizeLarge: { minHeight: 58, fontSize: '1.0625rem' },
        sizeSmall: { minHeight: 40, fontSize: '0.875rem', paddingInline: 14 },
        containedPrimary: {
          boxShadow: '0 6px 18px -8px rgba(232,78,27,0.9)',
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          // Cible tactile minimale confortable, même avec des gants
          minWidth: 48,
          minHeight: 48,
          '&:active': { transform: 'scale(0.94)' },
          transition: 'transform 120ms ease, background-color 160ms ease',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: NEUTRAL[850],
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backgroundImage: 'none',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 60,
          '@media (min-width:600px)': { minHeight: 64 },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: NEUTRAL[800],
          // 16px minimum : en dessous, iOS zoome automatiquement au focus
          fontSize: '1rem',
        },
        input: { paddingTop: 17, paddingBottom: 17 },
        notchedOutline: { borderColor: 'rgba(255,255,255,0.16)', borderWidth: 1.5 },
      },
    },

    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '1rem' } },
    },

    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: { sx: { maxHeight: '55vh', bgcolor: NEUTRAL[800] } },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: { root: { minHeight: 52, fontSize: '1rem' } },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          paddingLeft: 12,
          paddingRight: 12,
          paddingTop: 12,
          paddingBottom: 12,
        },
        head: {
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: NEUTRAL[300],
          backgroundColor: 'transparent',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, borderRadius: 10 },
      },
    },

    MuiCircularProgress: {
      defaultProps: { thickness: 4 },
    },

    MuiSvgIcon: {
      styleOverrides: {
        fontSizeLarge: { fontSize: '2rem' },
      },
    },
  },
})

export default theme
